"use client"

import { useEffect, useState } from "react"
import API from "../services/api"
import {
  ArrowLeft,
  Facebook,
  Instagram,
  Volume2,
  Wifi,
  Thermometer,
  Coffee,
  Building2,
  Maximize2,
  Ruler,
  ChevronLeft,
  ChevronRight,
  Users,
} from "lucide-react"

// Translation system
const translations = {
  fr: {
    back: "Retour",
    seminarSpaces: "Espaces",
    seminars: "Business",
    discoverSpaces: "Découvrez nos espaces professionnels pour vos événements d'entreprise",
    capacity: "Capacité",
    people: "personnes",
    area: "Superficie",
    height: "Hauteur sous plafond",
    meters: "m",
    defaultDescription:
      "Cet espace de séminaire est parfaitement équipé pour accueillir vos événements professionnels dans un cadre élégant et fonctionnel.",
    audiovisualEquipment: "Équipement audiovisuel",
    highSpeedWifi: "Wi-Fi haut débit",
    airConditioning: "Climatisation",
    cateringService: "Service de restauration",
    reserveRoom: "RÉSERVER UNE SALLE",
    technicalSheet: "FICHE TECHNIQUE",
    loadingSeminars: "Chargement des séminaires...",
    noSeminarSpaces: "Aucun espace de séminaire disponible",
    comeBackSoonSpaces: "Revenez bientôt pour découvrir nos espaces",
    contact: "Contact",
    address: "Adresse",
    reservations: "Réservations",
    wifi: "Wi-Fi",
    followUs: "Suivez-nous",
    network: "Réseau",
    password: "Mot de passe",
    availableAtReception: "Réseau Accor",
    addressLine1: "Avenue Majida Boulilla",
    addressLine2: "3000 SFAX, Tunisie",
    allRightsReserved: "Tous droits réservés",
    createdBy: "Créé par",
    seminarRooms: "Salles de Séminaire",
    roomName: "Nom de la Salle",
    description: "Description",
    amenities: "Équipements et Services",
    theatre: "Théâtre",
    salleU: "Salle U",
    salleReunion: "Salle Réunion",
    salleClasse: "Salle Classe",
    banquet: "Banquet",
    surface: "Surface",
    hauteur: "Hauteur",
  },
  en: {
    back: "Back",
    seminarSpaces: "Business",
    seminars: "Spaces",
    discoverSpaces: "Discover our professional spaces for your corporate events",
    capacity: "Capacity",
    people: "people",
    area: "Area",
    height: "Ceiling Height",
    meters: "m",
    defaultDescription:
      "This seminar space is perfectly equipped to host your professional events in an elegant and functional setting.",
    audiovisualEquipment: "Audiovisual equipment",
    highSpeedWifi: "High-speed Wi-Fi",
    airConditioning: "Air conditioning",
    cateringService: "Catering service",
    reserveRoom: "BOOK A ROOM",
    technicalSheet: "TECHNICAL SHEET",
    loadingSeminars: "Loading seminars...",
    noSeminarSpaces: "No seminar spaces available",
    comeBackSoonSpaces: "Come back soon to discover our spaces",
    contact: "Contact",
    address: "Address",
    reservations: "Reservations",
    wifi: "Wi-Fi",
    followUs: "Follow Us",
    network: "Network",
    password: "Password",
    availableAtReception: "accor network",
    addressLine1: "Avenue Majida Boulilla",
    addressLine2: "3000 SFAX, Tunisia",
    allRightsReserved: "All rights reserved",
    createdBy: "Created by",
    seminarRooms: "Seminar Rooms",
    roomName: "Room Name",
    description: "Description",
    amenities: "Amenities and Services",
    theatre: "Theatre",
    salleU: "U-Shape",
    salleReunion: "Meeting",
    salleClasse: "Classroom",
    banquet: "Banquet",
    surface: "Surface",
    hauteur: "Height",
  },
  ar: {
    back: "رجوع",
    seminarSpaces: "مساحات",
    seminars: "الندوات",
    discoverSpaces: "اكتشف مساحاتنا الاحترافية لفعاليات شركتك",
    capacity: "السعة",
    people: "شخص",
    area: "المساحة",
    height: "ارتفاع السقف",
    meters: "م",
    defaultDescription: "تم تجهيز مساحة الندوات هذه بشكل مثالي لاستضافة فعالياتك المهنية في بيئة أنيقة وعملية.",
    audiovisualEquipment: "معدات سمعية بصرية",
    highSpeedWifi: "واي فاي عالي السرعة",
    airConditioning: "تكييف",
    cateringService: "خدمة تقديم الطعام",
    reserveRoom: "احجز قاعة",
    technicalSheet: "الورقة الفنية",
    loadingSeminars: "جاري تحميل الندوات...",
    noSeminarSpaces: "لا توجد مساحات ندوات متاحة",
    comeBackSoonSpaces: "عد قريبًا لاكتشاف مساحاتنا",
    contact: "اتصل بنا",
    address: "العنوان",
    reservations: "الحجوزات",
    wifi: "واي فاي",
    followUs: "تابعونا",
    network: "الشبكة",
    password: "كلمة المرور",
    availableAtReception: "شبكة أكور",
    addressLine1: "شارع الكورنيش",
    addressLine2: "تونس، 3000 صفاقس",
    allRightsReserved: "جميع الحقوق محفوظة",
    createdBy: "تم إنشاؤه بواسطة",
    seminarRooms: "المساحات للندوات",
    roomName: "اسم المساحة",
    description: "الوصف",
    amenities: "المرافق والخدمات",
    theatre: "مسرح",
    salleU: "حرف U",
    salleReunion: "اجتماع",
    salleClasse: "فصل دراسي",
    banquet: "حفل عشاء",
    surface: "السطح",
    hauteur: "الارتفاع",
  },
}

const languages = [
  { code: "fr", name: "Français", flag: "/images/fr-flag-v2.png" },
  { code: "en", name: "English", flag: "/images/en-flag-v2.png" },
  { code: "ar", name: "العربية", flag: "/images/ar-flag-v2.png" },
]

const SeminaireClient = () => {
  const [seminaires, setSeminaires] = useState([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentLanguage, setCurrentLanguage] = useState("fr")
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const [pageContent, setPageContent] = useState(null)
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [carouselImages, setCarouselImages] = useState([])

  const t = (key) => translations[currentLanguage][key] || translations.fr[key] || key

  const fetchSeminaires = async () => {
    try {
      setIsLoading(true)
      const res = await API.get("/seminaires")
      setSeminaires(res.data)
      const allImages = []
      res.data.forEach((seminaire) => {
        if (seminaire.images && seminaire.images.length > 0) {
          seminaire.images.forEach((image) => {
            allImages.push({
              url: image,
              name: seminaire.nom,
            })
          })
        }
      })
      setCarouselImages(allImages)
      setIsLoaded(true)
    } catch (error) {
      console.error("Erreur chargement des séminaires:", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const res = await API.get("/page-contents/page/Seminaire")
        setPageContent(res.data)
      } catch (err) {
        console.error("Error fetching seminaire page content:", err)
        setPageContent(null)
      }
    }
    fetchPageContent()
  }, [])

  useEffect(() => {
    fetchSeminaires()
    const timer = setTimeout(() => {
      setIsLoaded(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const savedLanguage = localStorage.getItem("IBIS-language")
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage)
      document.documentElement.dir = savedLanguage === "ar" ? "rtl" : "ltr"
      document.documentElement.lang = savedLanguage
    }
  }, [])

  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("IBIS-language", langCode)
    setShowLanguageDropdown(false)
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = langCode
  }

  const handlePrevImage = () => {
    setCarouselIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)
  }

  const handleNextImage = () => {
    setCarouselIndex((prev) => (prev + 1) % carouselImages.length)
  }

  const getCurrentLanguage = () => languages.find((lang) => lang.code === currentLanguage)

  useEffect(() => {
    if (carouselImages.length === 0) return

    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselImages.length)
    }, 5000) // Change image every 5 seconds

    return () => clearInterval(interval)
  }, [carouselImages])

  return (
    <div className={`hotel-app8 ${currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <style jsx>{`
        /* Language dropdown styles */
        .language-selector {
          position: absolute;
          top: 15px;
          left: 15px;
          z-index: 20;
        }

        .language-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .language-toggle:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.05);
        }

        .language-flag {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }

        .language-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          background: rgba(0, 0, 0, 0.9);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 150px;
          z-index: 1000;
          backdrop-filter: blur(10px);
          margin-top: 5px;
        }

        .language-option {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          cursor: pointer;
          transition: background-color 0.2s;
          gap: 8px;
          color: white;
          font-size: 14px;
        }

        .language-option:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .language-option.active {
          background: rgba(255, 255, 255, 0.2);
        }

        .flag-small {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }

        /* Loading Spinner */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 200px;
          gap: 16px;
          color: #555;
        }

        .loading-spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #e72c3d;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Add carousel styles */
        .carousel-container {
          position: relative;
          width: 100%;
          height: 400px;
          background: #000;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 3rem;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .carousel-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: opacity 0.5s ease-in-out;
        }

        .carousel-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
          padding: 2rem 1.5rem;
          color: white;
        }

        .carousel-title {
          font-size: 1.8rem;
          font-weight: 600;
          margin: 0;
        }

        .carousel-nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          z-index: 10;
        }

        .carousel-nav-button:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: translateY(-50%) scale(1.1);
        }

        .carousel-nav-button.prev {
          left: 1rem;
        }

        .carousel-nav-button.next {
          right: 1rem;
        }

        .carousel-dots {
          position: absolute;
          bottom: 1rem;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 0.5rem;
          z-index: 10;
        }

        .carousel-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
          padding: 0;
        }

        .carousel-dot.active {
          background: white;
          width: 30px;
          border-radius: 5px;
        }

        .carousel-dot:hover {
          background: rgba(255, 255, 255, 0.8);
        }

        .rtl .carousel-nav-button.prev {
          left: auto;
          right: 1rem;
        }

        .rtl .carousel-nav-button.next {
          right: auto;
          left: 1rem;
        }

        @media (max-width: 768px) {
          .carousel-container {
            height: 250px;
            margin-bottom: 2rem;
          }

          .carousel-title {
            font-size: 1.3rem;
          }

          .carousel-nav-button {
            width: 40px;
            height: 40px;
          }

          .carousel-nav-button.prev {
            left: 0.5rem;
          }

          .carousel-nav-button.next {
            right: 0.5rem;
          }
        }

        @media (max-width: 480px) {
          .carousel-container {
            height: 180px;
            margin-bottom: 1.5rem;
          }

          .carousel-title {
            font-size: 1rem;
          }

          .carousel-overlay {
            padding: 1rem;
          }

          .carousel-nav-button {
            width: 35px;
            height: 35px;
          }

          .carousel-dots {
            bottom: 0.5rem;
            gap: 0.3rem;
          }

          .carousel-dot {
            width: 8px;
            height: 8px;
          }

          .carousel-dot.active {
            width: 24px;
          }
        }

        /* RTL Support */
        .rtl {
          direction: rtl;
        }
        .rtl .language-selector {
          left: auto;
          right: 15px;
        }
        .rtl .language-dropdown {
          left: auto;
          right: 0;
          text-align: right;
        }
        .rtl .header-back-link {
          flex-direction: row-reverse;
        }
        .rtl .header-back-link svg {
          margin-left: 8px;
          margin-right: 0;
        }
        .rtl .welcome-banner h1 {
          text-align: right;
        }
        .rtl .welcome-banner p {
          text-align: right;
        }
        .rtl .empty-state {
          text-align: right;
        }

        /* Main Container */
        .hotel-app8 {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 0;
          background-color: #f8f9fa;
        }

        .hotel-app8::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: url("https://itbafa.com/images/shirakaba_renewal2016-51-karuizawa-prince-hotel-west.jpg");
          background-size: cover;
          background-position: center;
          filter: blur(3px);
          opacity: 0.15;
          z-index: -1;
        }

        /* Header */
        .app-header {
          background-color: white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          padding: 1rem;
          position: sticky;
          top: 0;
          z-index: 100;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo-container {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .header-back-link {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #e72c3d;
          font-weight: 500;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .header-back-link:hover {
          color: #c01832;
        }

        .logo {
          height: 50px;
          width: auto;
        }

        /* Main Content */
        .app-main {
          flex: 1;
          padding: 2rem 1rem;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
        }

        /* Welcome Banner */
        .welcome-banner {
          text-align: center;
          margin-bottom: 3rem;
          padding: 2rem 0;
        }

        .welcome-banner h1 {
          font-size: 2.5rem;
          font-weight: 300;
          color: #343a40;
          margin-bottom: 0.5rem;
        }

        .welcome-banner h1 span {
          font-weight: 600;
          color: #e72c3d;
        }

        .welcome-banner p {
          font-size: 1.2rem;
          color: #6c757d;
        }

        /* Page Content */
        .page-content-seminaire {
          margin: 2rem 0;
          text-align: center;
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .page-content-image {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .page-content-description {
          font-size: 1rem;
          color: #555;
          line-height: 1.6;
        }

        /* Table layout for seminaires */
        .seminaires-table-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          margin: 3rem 0;
          overflow-x: auto;
        }

        .seminaires-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
        }

        .table-header {
          background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%);
          border-bottom: 2px solid #ddd;
        }

        .table-header th {
          padding: 1.5rem 1rem;
          text-align: center;
          font-weight: 600;
          color: #343a40;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-right: 1px solid #ddd;
        }

        .table-header th:last-child {
          border-right: none;
        }

        .table-header th:first-child {
          text-align: left;
          background: #f8f9fa;
        }

        .table-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.5rem;
          color: #e72c3d;
        }

        .table-body tr {
          border-bottom: 1px solid #e9ecef;
          transition: background-color 0.2s ease;
        }

        .table-body tr:hover {
          background-color: #f8f9fa;
        }

        .table-body td {
          padding: 1.25rem 1rem;
          text-align: center;
          color: #555;
          font-size: 0.95rem;
        }

        .table-body td:first-child {
          text-align: left;
          font-weight: 600;
          color: #e72c3d;
          background: #f8f9fa;
          position: sticky;
          left: 0;
          z-index: 10;
        }

        .table-body td:first-child a {
          color: #e72c3d;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .table-body td:first-child a:hover {
          color: #c01832;
          text-decoration: underline;
        }

        .capacity-value {
          font-weight: 600;
          color: #343a40;
        }

        .empty-capacity {
          color: #ccc;
          font-size: 1.2rem;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #6c757d;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
          color: #343a40;
        }

        /* Amenities Section */
        .amenities-section {
          margin: 3rem 0;
          padding: 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .amenities-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #343a40;
          text-align: center;
          margin-bottom: 2rem;
        }

        .amenities-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }

        .amenity-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          background: linear-gradient(135deg, #e72c3d 0%, #c01832 100%);
          color: white;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .amenity-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 16px rgba(231, 44, 61, 0.3);
        }

        .amenity-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 50px;
          height: 50px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          flex-shrink: 0;
        }

        .amenity-text {
          font-weight: 500;
          font-size: 1rem;
        }

        /* Footer */
        .app-footer {
          background: linear-gradient(135deg, #e72c3d 0%, #c01832 100%);
          color: white;
          padding: 3rem 1rem;
          margin-top: 3rem;
        }

        .footer-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          margin-bottom: 2rem;
        }

        .footer-section h4 {
          font-size: 1.1rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .footer-section p {
          margin-bottom: 0.5rem;
          opacity: 0.9;
          font-size: 0.95rem;
        }

        .social-links {
          display: flex;
          gap: 1rem;
          margin-top: 1rem;
        }

        .social-links a {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          color: white;
          transition: all 0.3s ease;
        }

        .social-links a:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-3px);
        }

        .copyright {
          text-align: center;
          padding-top: 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          font-size: 0.875rem;
          opacity: 0.8;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .app-main {
            padding: 1rem;
          }

          .welcome-banner h1 {
            font-size: 2rem;
          }

          .welcome-banner p {
            font-size: 1rem;
          }

          .seminaires-table-container {
            overflow-x: auto;
          }

          .table-header th {
            padding: 1rem 0.75rem;
            font-size: 0.8rem;
          }

          .table-body td {
            padding: 1rem 0.75rem;
            font-size: 0.85rem;
          }

          .amenities-grid {
            grid-template-columns: 1fr;
          }

          .footer-content {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .social-links {
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .welcome-banner h1 {
            font-size: 1.5rem;
          }

          .table-header th {
            padding: 0.75rem 0.5rem;
            font-size: 0.7rem;
          }

          .table-body td {
            padding: 0.75rem 0.5rem;
            font-size: 0.75rem;
          }

          .table-icon {
            margin-bottom: 0.25rem;
          }

          .table-icon svg {
            width: 16px;
            height: 16px;
          }

          .amenities-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Language Selector */}
      <div className="language-selector">
        <button className="language-toggle" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
          <img
            src={getCurrentLanguage()?.flag || "/placeholder.svg"}
            alt={getCurrentLanguage()?.name}
            className="language-flag"
          />
          <span>{getCurrentLanguage()?.code.toUpperCase()}</span>
        </button>
        {showLanguageDropdown && (
          <div className="language-dropdown">
            {languages.map((lang) => (
              <div
                key={lang.code}
                className={`language-option ${currentLanguage === lang.code ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation()
                  changeLanguage(lang.code)
                }}
              >
                <img src={lang.flag || "/placeholder.svg"} alt={lang.name} className="flag-small" />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Header */}
      <header className="app-header">
        <a href="/Home" className="header-back-link">
          <ArrowLeft width="24" height="24" />
          {t("back")}
        </a>
        <div className="logo-container">
          <img src="/images/logo2.png" alt="Ibis Logo" className="logo" />
        </div>
        <div></div>
      </header>

      {/* Main Content */}
      <main className="app-main">
        {/* Welcome Banner */}
        <div className="welcome-banner">
          <h1>
            <span>{t("seminarSpaces")}</span> {t("seminars")}
          </h1>
          <p>{t("discoverSpaces")}</p>
        </div>

        {/* Page Content */}
        {pageContent && (
          <div className="page-content-seminaire">
            {pageContent.image && (
              <img
                src={pageContent.image || "/placeholder.svg"}
                alt="Seminaire"
                className="page-content-image"
                onError={(e) => (e.target.src = "/placeholder.svg")}
              />
            )}
            {pageContent.description && (
              <div className="page-content-description" dangerouslySetInnerHTML={{ __html: pageContent.description }} />
            )}
          </div>
        )}

        {carouselImages.length > 0 && (
          <div className="carousel-container">
            <img
              src={carouselImages[carouselIndex]?.url || "/placeholder.svg"}
              alt={carouselImages[carouselIndex]?.name}
              className="carousel-image"
              onError={(e) => (e.target.src = "/placeholder.svg")}
            />
            <div className="carousel-overlay">
              <h2 className="carousel-title">{carouselImages[carouselIndex]?.name}</h2>
            </div>
            <button className="carousel-nav-button prev" onClick={handlePrevImage} aria-label="Previous image">
              <ChevronLeft width="24" height="24" />
            </button>
            <button className="carousel-nav-button next" onClick={handleNextImage} aria-label="Next image">
              <ChevronRight width="24" height="24" />
            </button>
            <div className="carousel-dots">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === carouselIndex ? "active" : ""}`}
                  onClick={() => setCarouselIndex(index)}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t("loadingSeminars")}</p>
          </div>
        ) : seminaires.length > 0 ? (
          <>
            <div className="seminaires-table-container">
              <table className="seminaires-table">
                <thead className="table-header">
                  <tr>
                    <th>{t("roomName")}</th>
                    <th>
                      <div className="table-icon">
                        <Maximize2 width="20" height="20" />
                      </div>
                      {t("surface")}
                    </th>
                    <th>
                      <div className="table-icon">
                        <Ruler width="20" height="20" />
                      </div>
                      {t("hauteur")}
                    </th>
                    <th>
                      <div className="table-icon">
                        <Building2 width="20" height="20" />
                      </div>
                      {t("theatre")}
                    </th>
                    <th>
                      <div className="table-icon">
                        <Building2 width="20" height="20" />
                      </div>
                      {t("salleU")}
                    </th>
                    <th>
                      <div className="table-icon">
                        <Building2 width="20" height="20" />
                      </div>
                      {t("salleReunion")}
                    </th>
                    <th>
                      <div className="table-icon">
                        <Building2 width="20" height="20" />
                      </div>
                      {t("salleClasse")}
                    </th>
                    <th>
                      <div className="table-icon">
                        <Coffee width="20" height="20" />
                      </div>
                      {t("banquet")}
                    </th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {seminaires.map((seminaire) => (
                    <tr key={seminaire._id}>
                      <td>
                        <a href={`/seminaire/${seminaire._id}`}>{seminaire.nom}</a>
                      </td>
                      <td className="capacity-value">{seminaire.surface} m²</td>
                      <td className="capacity-value">{seminaire.hauteur} m</td>
                      <td className="capacity-value">
                        {seminaire.theatre > 0 ? (
                          <>
                            {seminaire.theatre}{" "}
                            <Users width="16" height="16" style={{ display: "inline", marginLeft: "4px" }} />
                          </>
                        ) : (
                          <span className="empty-capacity">-</span>
                        )}
                      </td>
                      <td className="capacity-value">
                        {seminaire.salle_u > 0 ? (
                          <>
                            {seminaire.salle_u}{" "}
                            <Users width="16" height="16" style={{ display: "inline", marginLeft: "4px" }} />
                          </>
                        ) : (
                          <span className="empty-capacity">-</span>
                        )}
                      </td>
                      <td className="capacity-value">
                        {seminaire.salle_reunion > 0 ? (
                          <>
                            {seminaire.salle_reunion}{" "}
                            <Users width="16" height="16" style={{ display: "inline", marginLeft: "4px" }} />
                          </>
                        ) : (
                          <span className="empty-capacity">-</span>
                        )}
                      </td>
                      <td className="capacity-value">
                        {seminaire.salle_classe > 0 ? (
                          <>
                            {seminaire.salle_classe}{" "}
                            <Users width="16" height="16" style={{ display: "inline", marginLeft: "4px" }} />
                          </>
                        ) : (
                          <span className="empty-capacity">-</span>
                        )}
                      </td>
                      <td className="capacity-value">
                        {seminaire.banquet > 0 ? (
                          <>
                            {seminaire.banquet}{" "}
                            <Users width="16" height="16" style={{ display: "inline", marginLeft: "4px" }} />
                          </>
                        ) : (
                          <span className="empty-capacity">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Amenities Section */}
  
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>{t("noSeminarSpaces")}</h3>
            <p>{t("comeBackSoonSpaces")}</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t("contact")}</h4>
            <p>+216 70 241 000</p>
            <p>ha094@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("address")}</h4>
            <p>{t("addressLine1")}</p>
            <p>{t("addressLine2")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("reservations")}</h4>
            <p>+216 70 241 000</p>
            <p>ha094@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("wifi")}</h4>
            <p>{t("availableAtReception")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/ibissfax" aria-label="Facebook">
                <Facebook width="24" height="24" />
              </a>
              <a href="https://www.instagram.com/ibis_sfax" aria-label="Instagram">
                <Instagram width="24" height="24" />
              </a>
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>
            © {new Date().getFullYear()} IBIS Sfax. {t("allRightsReserved")}.
            <br />
            {t("createdBy")}{" "}
            <a href="https://www.itbafa.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/images/itbafa_logo_white.png"
                alt="ITBAFA Logo"
                style={{ height: "20px", verticalAlign: "middle", marginLeft: "5px" }}
              />
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default SeminaireClient
